<script setup lang="ts">
import { tpgIconSave } from '@tpg/grammar-web-icons';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

/**
 * This component represents the modal that pops up when the user either:
 *
 *    1. Clicks "Create conversation", or
 *    2. Clicks "Conversation details"
 *
 * We represent these two modes with `create` and `edit`, and seed form state
 * differently depending on which path opened the modal.
 *
 * Like the reference OperationModal work, this modal is treated as a form
 * driven by `NoraForm`/vee-validate rather than direct component-local field
 * mutation. That lets us keep validation, button enabled state, and seeded
 * values in one place.
 *
 * One key detail here is the create-mode member seed. The backend still adds
 * the logged-in user as an admin when creating a conversation, but the modal
 * now shows that user up front as a non-removable admin so the UI matches the
 * actual save behavior.
 */
import type { GrammarTextFieldExpose } from '@nora-monorepo/grammar-ui';
import {
  GrammarFooter,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
} from '@nora-monorepo/grammar-ui';
import type {
  ConversationCreatePayloadDTO,
  ConversationResultDTO,
  ConversationUpdatePayloadDTO,
  LocalRoleResultDTO,
} from '@nora-monorepo/nora-api-client';
import {
  createConversationMemberListSchema,
  createConversationTitleSchema,
  NoraConversationTitleInputField,
  NoraForm,
  useApiStore,
  useGenericUserStore,
  useSearchStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import {
  ConversationModalActionState,
  getDefaultConversationAdminRole,
  getDefaultConversationRole,
} from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

import type {
  ConversationMembers,
  ConversationModalFormValues,
} from './conversation-modal-types';
import ConversationMemberField from './ConversationMemberField.vue';

interface ConversationModalSubmittedValues {
  conversationTitle?: string;
  members?: ConversationMembers;
}

const props = withDefaults(defineProps<{
  isOpen?: boolean;
  mode?: ConversationModalActionState;
  conversation?: ConversationResultDTO;
  readOnly?: boolean;
  initialUsers?: ConversationMembers['users'][number]['user'][];
  initialGroups?: ConversationMembers['groups'][number]['group'][];
}>(), {
  isOpen: false,
  mode: ConversationModalActionState.Create,
  conversation: undefined,
  readOnly: false,
  initialUsers: () => [],
  initialGroups: () => [],
});

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const api = useApiStore();
const conversationStore = useConversationStore();
const genericUserStore = useGenericUserStore();
const searchStore = useSearchStore();
const toastStore = useToastStore();

const conversationTitleRef = ref<GrammarTextFieldExpose | null>(null);
const localRoles = ref<LocalRoleResultDTO[]>([]);
const isSaving = ref(false);

/// A simple counter, indicating the current iteration/number of openings of the modal.
const memberFieldSession = ref(0);

const inCreateMode = computed(() => {
  return props.mode === ConversationModalActionState.Create;
});

const connectedToOperation = computed(() => {
  return Boolean(props.conversation?.operationId);
});

const readOnly = computed(() => {
  return props.readOnly;
});

/**
 * This is a unique ID for a given member field instance. It is used to force a re-render of the member field when the modal is opened, so that the member field can be seeded with fresh data each time.
 *
 * The member field is a complex component that manages its own state, so we need to force a re-render when the modal is opened to ensure that it is seeded with the correct data.
 *
 * The member field is keyed by the session, mode, and conversation ID (or 'create' if in create mode). This ensures that the member field is re-rendered when the modal is opened, and that it is seeded with the correct data for the current mode and conversation.
 */
const memberFieldKey = computed(() => {
  return [
    memberFieldSession.value,
    props.mode,
    props.conversation?.id ?? 'create',
  ].join(':');
});

const modalHeader = computed(() => {
  if (inCreateMode.value) {
    return t('conversationDialog.createTitle');
  }

  if (!props.conversation?.id) {
    return t('conversationDialog.editTitle', ['']);
  }

  return t('conversationDialog.editTitle', [
    conversationStore.getConversationTitle(props.conversation.id) ?? '',
  ]);
});

const validationSchema = computed(() => yup.object({
  conversationTitle: createConversationTitleSchema(t).notRequired().nonNullable(),
  members: createConversationMemberListSchema(
    t,
    new Set(
      localRoles.value
        .filter(localRole => localRole.defaultAdmin)
        .map(localRole => localRole.id),
    ),
    new Set(localRoles.value.map(localRole => localRole.id)),
  ),
}));

/**
 * To be used primarily on mount of existing conversation data. Transforms the DTO into a locally usable data format.
 * @param conversation
 */
function createMembersFromConversation(
  conversation: ConversationResultDTO,
): ConversationMembers {
  return {
    users: conversation.users.map(member => ({
      user: member.user,
      role: member.localRole.id,
    })),
    groups: conversation.groups.map(member => ({
      group: member.group,
      role: member.localRole.id,
    })),
  };
}

/**
 * To be used primarily on mount of the modal to seed the member field with either the existing conversation data or the create-mode seed.
 * @returns {ConversationMembers} The initial members for the member field.
 */
function getInitialMembersForField(): ConversationMembers {
  if (inCreateMode.value) {
    return localRoles.value.length > 0
      ? createSeedMembers()
      : { users: [], groups: [] };
  }

  return props.conversation
    ? createMembersFromConversation(props.conversation)
    : { users: [], groups: [] };
}

/**
 * Creates what is effectively a unique string out of the id/role combinations.
 *
 * This string is a 'signature' of the given member list, which can be compared to another signature to determine if changes have been made.
 * @param members
 */
function createSignatureFromMembersList(members: ConversationMembers): string {
  return [
    ...members.users.map(member => `user:${member.user.id}:${member.role}`),
    ...members.groups.map(member => `group:${member.group.id}:${member.role}`),
  ].sort().join('|');
}

/**
 * Indicates if there are any meaningful changes made to the form.
 *
 * Used to determine if the Save button should be disabled or not.
 * @param formValues
 * @returns {true} if in create mode
 * @returns {true} if meaningful changes have been made in edit mode
 * @returns {false} if no meaningful changes have been made
 */
function hasBeenMeaningfullyChangedInEditMode(
  formValues: ConversationModalSubmittedValues,
): boolean {
  if (inCreateMode.value || !props.conversation) {
    return true;
  }

  const initialMembers = createMembersFromConversation(props.conversation);
  const currentMembers = formValues.members ?? { users: [], groups: [] };

  return formValues.conversationTitle !== (props.conversation.title ?? '')
    || createSignatureFromMembersList(currentMembers) !== createSignatureFromMembersList(initialMembers);
}

/**
 * Creates the default create-mode member seed, including the logged-in user as
 * a visible non-removable admin.
 */
function createSeedMembers(): ConversationMembers {
  const defaultRoleId = getDefaultConversationRole(localRoles.value).id;
  const defaultAdminRoleId = getDefaultConversationAdminRole(localRoles.value).id;
  const currentUser = genericUserStore.user;

  const seededUsers: ConversationMembers['users'] = currentUser
    ? [{
        user: currentUser,
        role: defaultAdminRoleId,
        nonRemovable: true,
      }]
    : [];

  for (const user of props.initialUsers) {
    const hasUser = seededUsers.some(member => member.user.id === user.id);

    if (hasUser) {
      continue;
    }

    seededUsers.push({
      user,
      role: defaultRoleId,
    });
  }

  return {
    users: seededUsers,
    groups: props.initialGroups.map(group => ({
      group,
      role: defaultRoleId,
    })),
  };
}

/**
 * Creates the relevant initial form state for `create` vs `edit` mode.
 */
function seedInitialFormValues(): ConversationModalFormValues {
  if (!inCreateMode.value) {
    if (!props.conversation) {
      throw new Error('Conversation is required in edit mode');
    }

    return {
      conversationTitle: props.conversation.title ?? '',
      members: createMembersFromConversation(props.conversation),
    };
  }

  return {
    conversationTitle: '',
    members: createSeedMembers(),
  };
}

/**
 * Converts form values into the create payload expected by the conversation
 * API. The non-removable self member is intentionally omitted because the
 * backend appends it during create.
 */
function createConversationPayload(
  formValues: ConversationModalFormValues,
): ConversationCreatePayloadDTO {
  const normalizedTitle = formValues.conversationTitle.trim();

  return {
    title: normalizedTitle.length > 0 ? normalizedTitle : undefined,
    users: formValues.members.users
      .filter(member => !member.nonRemovable)
      .map(member => ({
        userId: member.user.id,
        localRoleId: member.role,
      })),
    groups: formValues.members.groups.map(member => ({
      groupId: member.group.id,
      localRoleId: member.role,
    })),
  };
}

/**
 * Converts form values into the update payload expected by the conversation
 * API. An empty title is sent as `null` so the API clears it; `undefined`
 * would leave the existing title in place.
 */
function createUpdateConversationPayload(
  formValues: ConversationModalFormValues,
): ConversationUpdatePayloadDTO {
  const normalizedTitle = formValues.conversationTitle.trim();

  return {
    title: normalizedTitle.length > 0 ? normalizedTitle : null,
    users: formValues.members.users.map(member => ({
      userId: member.user.id,
      localRoleId: member.role,
    })),
    groups: formValues.members.groups.map(member => ({
      groupId: member.group.id,
      localRoleId: member.role,
    })),
  };
}

/**
 * Local role data is loaded on modal show so each caller can own modal state
 * directly without a shared modal controller/store.
 */
async function loadLocalRoles(): Promise<void> {
  const response = await api.unwrap(
    api.nexusRole.listLocalRoles({ type: 'CONVERSATION' }),
  );

  if (!response.data) {
    throw new Error('Could not load conversation roles');
  }

  localRoles.value = response.data;
}

async function createConversation(
  formValues: ConversationModalFormValues,
): Promise<void> {
  isSaving.value = true;

  try {
    const response = await api.unwrap(
      api.conversation.createConversation({
        conversationCreatePayloadDTO: createConversationPayload(formValues),
      }),
    );

    if (!response.data) {
      throw new Error('API did not return a created conversation');
    }

    conversationStore.addConversation(response.data, true);
    onClose();
  }
  finally {
    isSaving.value = false;
  }
}

async function updateConversation(
  formValues: ConversationModalFormValues,
): Promise<void> {
  if (isSaving.value) {
    return;
  }

  if (!props.conversation?.id) {
    throw new Error('Conversation is required to update a conversation');
  }

  isSaving.value = true;

  try {
    const response = await api.unwrap(
      api.conversation.updateConversation({
        conversationId: props.conversation.id,
        conversationUpdatePayloadDTO: createUpdateConversationPayload(formValues),
      }),
    );

    if (!response.data) {
      throw new Error('API did not return an updated conversation');
    }

    conversationStore.updateConversation(response.data, response.data);
    onClose();
  }
  finally {
    isSaving.value = false;
  }
}

async function saveConversation(values: ConversationModalSubmittedValues) {
  if (isSaving.value) {
    return;
  }

  const normalizedValues: ConversationModalFormValues = {
    conversationTitle: values.conversationTitle ?? '',
    members: values.members ?? { users: [], groups: [] },
  };

  try {
    if (inCreateMode.value) {
      await createConversation(normalizedValues);
      return;
    }

    await updateConversation(normalizedValues);
  }
  catch (error) {
    console.error(error);
    toastStore.addError({
      message: t('errors.createConversationError'),
    });
  }
}

function onClose() {
  searchStore.clearUsersAndGroupsResults();
  emit('close');
}

/**
 * Re-seeds the form each time the modal opens so create/edit callers can mount
 * a normal prop-driven modal instance and pass fresh seed data directly.
 */
async function onModalShow(
  resetForm: (
    state?: { values?: ConversationModalFormValues },
  ) => void,
  validateForm: () => Promise<unknown>,
) {
  await loadLocalRoles();
  resetForm({
    values: seedInitialFormValues(),
  });

  /** We increment this so that we ensure a re-render of the member fields (child component). See {@link memberFieldKey} */
  memberFieldSession.value += 1;

  await nextTick();
  await validateForm();
  await nextTick();

  requestAnimationFrame(() => {
    conversationTitleRef.value?.focus();
  });
}
</script>

<template>
  <NoraForm
    :validation-schema="validationSchema"
    :initial-values="{
      conversationTitle: '',
      members: { users: [], groups: [] },
    }"
    :validate-on-mount="true"
    @form-submitted="saveConversation"
  >
    <template #default="{ formValues, canSubmit, submitForm, validateForm, resetForm }">
      <GrammarModal
        :is-open="props.isOpen"
        :label="modalHeader"
        :show-close-button="true"
        :prevent-close-on-click-outside="true"
        @show="onModalShow(
          resetForm as (state?: { values?: ConversationModalFormValues }) => void,
          validateForm as () => Promise<unknown>,
        )"
        @close="onClose"
      >
        <template #content>
          <div
            class="flex h-[calc(90vh-150px)] flex-col overflow-hidden p-3 max-w-[95%] min-w-[max(304px,80vw)] lg:min-w-[60vw]"
          >
            <span class="mb-2 flex flex-col">
              <NoraConversationTitleInputField
                ref="conversationTitleRef"
                name="conversationTitle"
                data-testid="conversationDialog.titleLabel"
                :readonly="readOnly"
              />
            </span>

            <div class="flex-1 overflow-auto">
              <ConversationMemberField
                :key="memberFieldKey"
                members="members"
                :initial-members="getInitialMembersForField()"
                :enabled="Boolean(props.isOpen)"
                :read-only="readOnly"
                :is-connected-to-operation="connectedToOperation"
                :local-roles="localRoles"
              />
            </div>
          </div>
        </template>

        <template v-if="!readOnly" #footer>
          <GrammarFooter>
            <template #left-button>
              <GrammarSecondaryButton
                :label="t('common.cancel')"
                @click="onClose"
              />
            </template>
            <template #right-button>
              <GrammarPrimaryButton
                :label="inCreateMode ? t('common.create') : t('common.save')"
                :icon-template="tpgIconSave"
                :disabled="!canSubmit || isSaving || (!inCreateMode && !hasBeenMeaningfullyChangedInEditMode(formValues))"
                @click="submitForm"
              />
            </template>
          </GrammarFooter>
        </template>
      </GrammarModal>
    </template>
  </NoraForm>
</template>
